<script setup>
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store.js';

const route = useRoute();
const router = useRouter();
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const accountId = computed(() => Number(route.params.accountId));
const featureFlag = computed(() => route.meta?.featureFlag);

const featureUnavailable = computed(() => {
  const checker = isFeatureEnabledonAccount.value;
  if (typeof checker !== 'function') return false;
  if (!featureFlag.value || !accountId.value) return false;

  return !checker(accountId.value, featureFlag.value);
});

const openBillingSettings = () => {
  if (!accountId.value) return;
  router.push({
    name: 'billing_settings_index',
    params: { accountId: accountId.value },
  });
};
</script>

<template>
  <div
    class="flex flex-col w-full h-full m-0 overflow-auto bg-n-background font-inter ailab-white-surface"
  >
    <div
      v-if="featureUnavailable"
      class="ailab-disabled-state flex flex-col items-start justify-center mx-auto my-20 p-8"
    >
      <h2 class="text-xl font-semibold mb-2 text-n-slate-12">
        AiLab AI is disabled for this account
      </h2>
      <p class="text-sm text-n-slate-11 mb-5 max-w-xl">
        This workspace does not currently have AiLab AI enabled. You can
        still access this page to understand the capability, then enable it from
        billing/settings when ready.
      </p>
      <button type="button" class="ailab-disabled-action" @click="openBillingSettings">
        Open Billing Settings
      </button>
    </div>
    <div v-else class="flex items-start w-full h-full">
      <router-view />
    </div>
  </div>
</template>

<style scoped>
.ailab-disabled-state {
  width: min(860px, calc(100% - 48px));
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  background: #ffffff;
}

.ailab-disabled-action {
  padding: 10px 14px;
  border-radius: 10px;
  border: 1px solid #111827;
  background: #111827;
  color: #ffffff;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
}

.ailab-disabled-action:hover {
  background: #1f2937;
}

/* Force AiLab pages to always use light (white) surface, even in dark mode. */
.bg-n-background {
  background: #ffffff !important;
}

.ailab-white-surface {
  background: #ffffff !important;
  color-scheme: light;
  /* Force legacy WLAB tokens to light theme within AiLab area */
  --wlab-bg-primary: #ffffff;
  --wlab-bg-secondary: #f8fafc;
  --wlab-bg-tertiary: #f8fafc;
  --wlab-bg-card: #ffffff;
  --wlab-text-primary: #111827;
  --wlab-text-secondary: #334155;
  --wlab-text-muted: #64748b;
  --wlab-text-error: #be123c;
  --wlab-border-light: #e2e8f0;
  --wlab-border-default: #cbd5e1;
}

.ailab-white-surface :deep(.bg-n-background) {
  background: #ffffff !important;
}

/* Common surfaces used across ailab pages/tabs */
.ailab-white-surface :deep(.bg-n-solid-2) {
  background: #ffffff !important;
}

.ailab-white-surface :deep(.bg-n-solid-1),
.ailab-white-surface :deep(.dark\:bg-n-solid-1) {
  background: #ffffff !important;
}

.ailab-white-surface :deep(.bg-n-alpha-2) {
  background: rgba(15, 23, 42, 0.04) !important; /* slate-900 @ 4% */
}

.ailab-white-surface :deep(.bg-n-alpha-black2) {
  background: #ffffff !important;
}

/* Typography tokens */
.ailab-white-surface :deep(.text-n-slate-12) {
  color: #111827 !important;
}

.ailab-white-surface :deep(.text-n-slate-11),
.ailab-white-surface :deep(.text-n-slate-10),
.ailab-white-surface :deep(.text-n-slate-9) {
  color: #64748b !important;
}

/* Force "dark:" utility text colors to black inside AiLab */
.ailab-white-surface :deep(.dark\:text-white),
.ailab-white-surface :deep(.dark\:text-white\/70),
.ailab-white-surface :deep(.dark\:text-white\/60),
.ailab-white-surface :deep(.dark\:text-white\/50) {
  color: #000000 !important;
}

/* Force "dark:" utility backgrounds/borders to light inside AiLab */
.ailab-white-surface :deep(.dark\:bg-black\/20) {
  background: #ffffff !important;
}

.ailab-white-surface :deep(.dark\:border-white\/10) {
  border-color: #e2e8f0 !important;
}

/* Borders */
.ailab-white-surface :deep(.border-n-weak) {
  border-color: #e2e8f0 !important;
}

.ailab-white-surface :deep(.border-n-strong) {
  border-color: #cbd5e1 !important;
}

/* -------------------------------------------------------------------------- */
/* AiLab internal design language: match Settings/Reports (light only)       */
/* -------------------------------------------------------------------------- */

.ailab-white-surface :deep(.fin-card),
.ailab-white-surface :deep(.card),
.ailab-white-surface :deep(.section-card) {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 16px !important;
}

.ailab-white-surface :deep(.fin-card:hover:not(.expanded)),
.ailab-white-surface :deep(.section-card:hover) {
  background: #f8fafc !important;
}

.ailab-white-surface :deep(.fin-card-header),
.ailab-white-surface :deep(.section-header),
.ailab-white-surface :deep(.tab-header) {
  padding: 20px !important;
}

.ailab-white-surface :deep(.tab-header) {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 16px !important;
}

.ailab-white-surface :deep(.fin-card-title),
.ailab-white-surface :deep(.header-title) {
  color: #0f172a !important;
}

.ailab-white-surface :deep(.fin-card-description),
.ailab-white-surface :deep(.header-description),
.ailab-white-surface :deep(.header-desc) {
  color: #475569 !important;
}

.ailab-white-surface :deep(.fin-card-icon),
.ailab-white-surface :deep(.header-icon) {
  background: #f1f5f9 !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 14px !important;
  color: #0f172a !important;
}

.ailab-white-surface :deep(.fin-card-icon.basic) {
  background: rgba(59, 130, 246, 0.08) !important;
  border-color: rgba(59, 130, 246, 0.18) !important;
  color: #1d4ed8 !important;
}
.ailab-white-surface :deep(.fin-card-icon.persona) {
  background: rgba(168, 85, 247, 0.08) !important;
  border-color: rgba(168, 85, 247, 0.18) !important;
  color: #6d28d9 !important;
}
.ailab-white-surface :deep(.fin-card-icon.features) {
  background: rgba(16, 185, 129, 0.08) !important;
  border-color: rgba(16, 185, 129, 0.18) !important;
  color: #047857 !important;
}
.ailab-white-surface :deep(.fin-card-icon.deployment) {
  background: rgba(245, 158, 11, 0.1) !important;
  border-color: rgba(245, 158, 11, 0.2) !important;
  color: #b45309 !important;
}

.ailab-white-surface :deep(.fin-card-body),
.ailab-white-surface :deep(.section-body) {
  padding: 0 20px 20px !important;
}

.ailab-white-surface :deep(.form-label) {
  color: #0f172a !important;
  font-weight: 600 !important;
}

.ailab-white-surface :deep(.form-hint),
.ailab-white-surface :deep(.help-text) {
  color: #64748b !important;
}

.ailab-white-surface :deep(.form-input),
.ailab-white-surface :deep(.form-select),
.ailab-white-surface :deep(.form-textarea),
.ailab-white-surface :deep(.table-input) {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  color: #0f172a !important;
  border-radius: 10px !important;
}

.ailab-white-surface :deep(.form-input:focus),
.ailab-white-surface :deep(.form-select:focus),
.ailab-white-surface :deep(.form-textarea:focus),
.ailab-white-surface :deep(.table-input:focus) {
  border-color: #cbd5e1 !important;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12) !important;
}

.ailab-white-surface :deep(.form-actions) {
  background: rgba(255, 255, 255, 0.92) !important;
  backdrop-filter: blur(8px);
  border-top: 1px solid #e2e8f0 !important;
}

.ailab-white-surface :deep(.save-btn),
.ailab-white-surface :deep(.add-btn),
.ailab-white-surface :deep(.action-btn),
.ailab-white-surface :deep(.add-scenario-btn) {
  border-radius: 10px !important;
}

/* Back button icon/text should be black in AiLab */
.ailab-white-surface :deep(.back-btn) {
  color: #000000 !important;
}

/* Simulator/ABTest: ensure action text stays black */
.ailab-white-surface :deep(.secondary-btn),
.ailab-white-surface :deep(.primary-btn) {
  color: #000000 !important;
}

/* Any label should be black in AiLab */
.ailab-white-surface :deep(label) {
  color: #0f172a !important;
}

/* Report-style switch used in AiLab */
.ailab-white-surface :deep(.crt-switch) {
  width: 27px;
  height: 20px;
  border: none;
  border-radius: 15px;
  box-sizing: border-box;
  cursor: pointer;
  background: rgb(221, 221, 221);
  padding: 0;
  display: inline-block;
  position: relative;
}

.ailab-white-surface :deep(.crt-switch span) {
  position: absolute;
  display: block;
  top: 2px;
  left: 2px;
  border-radius: 50%;
  background-color: rgb(255, 255, 255);
  box-shadow:
    rgba(0, 0, 0, 0.05) 0 3px 3px 0,
    rgba(0, 0, 0, 0.1) 0 2px 2px 0,
    rgba(0, 0, 0, 0.05) 0 3px 2px 0;
  transition:
    left 0.2s ease-in-out,
    background-color 0.2s ease-in-out;
  will-change: left, background-color;
  width: 15px;
  height: 15px;
}

.ailab-white-surface :deep(.crt-switch.is-on) {
  background: #2f80ed;
}

.ailab-white-surface :deep(.crt-switch.is-on span) {
  left: 10px;
}
</style>
